import { getSpec } from '@services/scoreSpecs';
import { payloadNamesFromSpec } from './payloadColumns';
import { specIdForFeed } from './specForFeed';

// Which payload columns a feed publishes, according to the score spec that published it — the A2
// route.
//
// Why the spec and not the response: `columns` is additive, so a payload column arrives only if the
// request named it, and read #1 of an unseen feed has nothing to name it from. A drill-down is
// ALWAYS read #1 of the member's feed, and never gets a read #2 — the form still points at the
// parent, so Submit re-reads the parent — which is why the reasoning disclosure went missing there
// and only there. The feed id carries its spec id (specForFeed.js), so the names are one cheap GET
// away, before the ~24s read goes out.
//
// Here rather than in the page because a page component calling a service was the clearest single
// symptom of that file holding nine jobs. Nothing about this is page-shaped: feed id in, names out.
//
// Named `specPayloadFields` rather than `payloadFields` because this filesystem is case-insensitive
// and the latter collides with PayloadFields.js, the component that renders them. Different things,
// and now different names.

// What can be answered WITHOUT asking anyone. `undefined` is the third state and the only reason a
// caller can stay synchronous: not "no payload" but "nobody has asked yet".
//
// Fails closed for a combined feed (no spec of its own) and for a free-form `scores[].feed_name`,
// which deliberately keeps no spec id in the published name. Both are settled answers.
export const knownPayloadFieldsFor = (feedId) => (specIdForFeed(feedId) ? undefined : null);

// And the asking part: one GET.
//
// FAILS OPEN in every direction. An id that names no spec, a spec that declares nothing, a fetch
// that errors — all yield null, the request falls back to the guess it makes today, and the A1
// bootstrap in openMemberFeed is what catches it.
//
// Returns `{ resolved, cacheable }` rather than a bare value because a FAILED fetch must not be
// cached: a 500 is not an answer, and caching one would disable this route for the feed for the rest
// of the session over a blip. A settled null — spec read, declares nothing — is cacheable, and is
// the common case for a combined feed.
export const fetchPayloadFields = async (feedId) => {
  const specId = specIdForFeed(feedId);
  if (!specId) return { resolved: null, cacheable: true };

  const { status, spec } = await getSpec(specId);
  if (status !== 200 || !spec) return { resolved: null, cacheable: false };

  const names = payloadNamesFromSpec(spec.spec_json, feedId);
  return { resolved: names.length ? names : null, cacheable: true };
};
